import { inject } from '@angular/core';
import { type CanActivateFn, Router } from '@angular/router';

import { combineLatest, map, of, switchMap, take } from 'rxjs';

import { ProfileService } from '@core/services/profile.service';

import { PAGE_PATHS, type PermissionLevel, type PortalPage } from '@models/page-permission.model';
import { type TenantFeature } from '@models/tenant-config.model';

/**
 * Factory guard: lets the user in only if their per-page permission (none/read/write, set by an admin under
 * Einstellungen > Berechtigungen) reaches `level` on the page, otherwise /access-denied.
 *
 * Usage in route config:
 *   canActivate: [authGuard, chefGuard, pageGuard('time')]
 */
export const pageGuard = (page: PortalPage, level: PermissionLevel = 'read'): CanActivateFn => {
  return () => {
    const profile = inject(ProfileService);
    const router = inject(Router);

    return profile.hasPage(page, level).pipe(
      take(1),
      map((allowed) =>
        allowed ? true : router.createUrlTree(['/access-denied'], { queryParams: { reason: 'page-forbidden', page } }),
      ),
    );
  };
};

/** Pages that exist only when the tenant has the feature switched on (same rule as the routes' featureGuard). */
const PAGE_FEATURES: Partial<Record<PortalPage, TenantFeature>> = {
  absences: 'Absences',
  shifts: 'ShiftPlanning',
  shiftSwaps: 'ShiftSwap',
};

/**
 * Guard of the start route: sends the user to the first page they may open (navigation order, switched-off
 * features skipped), or to /access-denied when there is none. "None" can also mean the profile could not be loaded
 * (cold start, no connection): that is not a missing permission and lands on the retry page instead.
 */
export const homeGuard: CanActivateFn = () => {
  const profile = inject(ProfileService);
  const router = inject(Router);

  return combineLatest([
    profile.readablePages$(),
    profile.isFeatureEnabled('Absences'),
    profile.isFeatureEnabled('ShiftPlanning'),
    profile.isFeatureEnabled('ShiftSwap'),
  ]).pipe(
    take(1),
    switchMap(([pages, ...flags]) => {
      const enabled = new Set<TenantFeature>();
      (['Absences', 'ShiftPlanning', 'ShiftSwap'] as const).forEach((f, i) => flags[i] && enabled.add(f));
      const first = pages.find((p) => {
        const feature = PAGE_FEATURES[p];
        return !feature || enabled.has(feature);
      });
      if (first) {return of(router.parseUrl(PAGE_PATHS[first]));}
      return profile.roleAccess('employee').pipe(
        take(1),
        map((access) =>
          access === 'unreachable'
            ? router.createUrlTree(['/access-denied'], { queryParams: { reason: 'unreachable', returnUrl: '/' } })
            : router.createUrlTree(['/access-denied'], { queryParams: { reason: 'page-forbidden' } }),
        ),
      );
    }),
  );
};
